<template>
    <div class="quick-options-form">
        <flex-container
            size="full"
            height="full"
            :padding="Size.md"
        >
            <flex-container
                direction="column"
                :gap="null"
            >
                <text-heading>Snelle opties</text-heading>
                <text-description>
                    Je kunt snelle opties genereren om veelgestelde vragen gemakkelijk te stellen aan de assistent.
                    Dit helpt gebruikers sneller antwoord te krijgen op populaire onderwerpen zonder extra typen.
                </text-description>
            </flex-container>
            <flex-container>
                <quick-options-list
                    :quick-options="assistent.widget.quickOptions"
                    :english-support-enabled="assistent.behavior.englishSupportEnabled"
                    @option:delete="deleteQuickOption"
                />
            </flex-container>
            <flex-container>
                <div>
                    <button-outline @click="openQuickOptionModal?.toggleOverlay()">
                        <flex-container
                            align-items="center"
                            :gap="Size.sm"
                        >
                            <svg-icon
                                :icon="AddIcon"
                                :color="'primary'"
                                :size="IconSize.md"
                            />
                            Snelle optie toevoegen
                        </flex-container>
                    </button-outline>
                </div>
            </flex-container>
            <flex-container align-items="center" :gap="Size.sm">
                <svg-icon
                    :icon="InfoIcon"
                    :color="'gray-400'"
                    :size="IconSize.sm"
                />
                <text-decorator :color="'gray-400'" :size="FontSize.xs">
                    Wij adviseren om maximaal 4 opties toe te voegen.
                </text-decorator>
            </flex-container>

            <horizontal-line />

            <grid-container template="two-columns">
                <flex-container :padding-right="Size.sm">
                    <text-heading>Geef weer in plaats van welkomstbericht</text-heading>
                    <text-description>
                        Je kunt er voor kiezen om in plaats van het welkomst bericht, de snelle opties te tonen.
                        Hiermee creëer je een opvallende call-to-action wat direct conversie kan verhogen.
                    </text-description>

                    <flex-container>
                        <flex-container>
                            <input-group v-model="assistent.widget.showQuickOptionsInsteadOfGreeting">
                                <template #input>
                                    <switch-input />
                                    <text-decorator :size="FontSize.xs">
                                        {{ assistent.widget.showQuickOptionsInsteadOfGreeting ? 'Ingeschakeld' : 'Uitgeschakeld' }}
                                    </text-decorator>
                                </template>
                            </input-group>
                        </flex-container>
                        <flex-container direction="column">
                            <text-decorator :size="FontSize.xs" bold>
                                Voorbeeld
                            </text-decorator>
                            <img
                                src="@/ai/assistent/assets/features/showQuickOptionsInsteadOfGreeting.png"
                                alt="Voorbeeld"
                                style="object-fit: contain; max-width: 200px"
                            />
                        </flex-container>
                    </flex-container>
                </flex-container>

                <flex-container :padding-left="Size.sm">
                    <text-heading>
                        Ondersteuning van de Engelse taal
                        <country-flag
                            country-code="gb"
                            :size="IconSize.sm"
                        />
                    </text-heading>
                    <text-description>
                        Snelle opties zijn nu beschikbaar in zowel Nederlands als Engels, waarbij Nederlands de standaardtaal blijft.
                        De website-widget past de taal automatisch aan op basis van de browserinstellingen: Nederlands voor Nederlandstalige browsers, anders Engels.
                    </text-description>
                    <input-group
                        v-model="assistent.behavior.englishSupportEnabled"
                        :schema="boolean().required()"
                        inline
                    >
                        <template #input>
                            <switch-input />
                            <text-decorator :size="FontSize.xs">
                                {{ assistent.behavior.englishSupportEnabled ? 'Ingeschakeld' : 'Uitgeschakeld' }}
                            </text-decorator>
                        </template>
                    </input-group>
                </flex-container>
            </grid-container>
        </flex-container>
    </div>

    <form-validation v-slot="{ bindings: quickOptionsBindings }">
        <modal-dialog
            ref="openQuickOptionModal"
            label="Modal om snelle optie toe te voegen"
            description="Modal om snelle optie toe te voegen"
            :size="5"
        >
            <template #default>
                <flex-container>
                    <flex-container direction="column">
                        <text-heading>
                            Voeg een snelle optie toe
                        </text-heading>
                    </flex-container>
                    <card-expandable with-content is-opened-at-start>
                        <text-heading sub>
                            <country-flag
                                country-code="nl"
                                :size="IconSize.sm"
                            />
                            Nederlands
                        </text-heading>

                        <template #content>
                            <flex-container>
                                <input-group
                                    v-model.trim="form.shortMessage"
                                    :schema="string().max(255).required()"
                                >
                                    <template #label="{ bindings }">
                                        <label :for="bindings.ids.input">
                                            <text-decorator :size="FontSize.xs">
                                                Buttontekst (Wordt weergegeven in de chat)
                                            </text-decorator>
                                        </label>
                                    </template>
                                    <template #input>
                                        <text-input placeholder="Schrijf hier een kort Nederlands berichtje voor in de snelle optie" @keydown.enter="addQuickOption(quickOptionsBindings.isValid.value as any)">
                                            <template #prefix>
                                                <svg-icon
                                                    :icon="MessageMinusIcon"
                                                    :size="IconSize.sm"
                                                    is-custom-icon
                                                />
                                            </template>
                                        </text-input>
                                    </template>
                                </input-group>
                                <input-group
                                    v-model="form.message"
                                    :schema="string().max(255).required()"
                                >
                                    <template #label="{ bindings }">
                                        <label :for="bindings.ids.input">
                                            <text-decorator :size="FontSize.xs">
                                                Te sturen AI-vraag (Wordt gestuurd naar de AI)
                                            </text-decorator>
                                        </label>
                                    </template>
                                    <template #input>
                                        <text-input placeholder="Schrijf hier een Nederlands bericht voor naar de assistent" @keydown.enter="addQuickOption(quickOptionsBindings.isValid.value as any)">
                                            <template #prefix>
                                                <svg-icon
                                                    :icon="MessageLinesIcon"
                                                    :size="IconSize.sm"
                                                    is-custom-icon
                                                />
                                            </template>
                                        </text-input>
                                    </template>
                                </input-group>
                            </flex-container>
                        </template>
                    </card-expandable>
                    <card-expandable
                        v-if="assistent.behavior.englishSupportEnabled"
                        with-content
                        is-opened-at-start
                    >
                        <text-heading sub>
                            <country-flag
                                country-code="gb"
                                :size="IconSize.sm"
                            />
                            Engels
                        </text-heading>
                        <template #content>
                            <flex-container>
                                <input-group
                                    v-model.trim="form.englishShortMessage"
                                    :schema="string().max(255).required()"
                                >
                                    <template #label="{ bindings }">
                                        <label :for="bindings.ids.input">
                                            <text-decorator :size="FontSize.xs">
                                                Buttontekst (Wordt weergegeven in de chat)
                                            </text-decorator>
                                        </label>
                                    </template>
                                    <template #input>
                                        <text-input placeholder="Schrijf hier een kort Engels berichtje voor de snelle optie" @keydown.enter="addQuickOption(quickOptionsBindings.isValid.value as any)">
                                            <template #prefix>
                                                <svg-icon
                                                    :icon="MessageMinusIcon"
                                                    :size="IconSize.sm"
                                                    is-custom-icon
                                                />
                                            </template>
                                        </text-input>
                                    </template>
                                </input-group>
                                <input-group
                                    v-model.trim="form.englishMessage"
                                    :schema="string().max(255).required()"
                                >
                                    <template #label="{ bindings }">
                                        <label :for="bindings.ids.input">
                                            <text-decorator :size="FontSize.xs">
                                                Te sturen AI-vraag (Wordt gestuurd naar de AI)
                                            </text-decorator>
                                        </label>
                                    </template>
                                    <template #input>
                                        <text-input placeholder="Schrijf hier een Engels bericht voor naar de assistent" @keydown.enter="addQuickOption(quickOptionsBindings.isValid.value as any)">
                                            <template #prefix>
                                                <svg-icon
                                                    :icon="MessageLinesIcon"
                                                    :size="IconSize.sm"
                                                    is-custom-icon
                                                />
                                            </template>
                                        </text-input>
                                    </template>
                                </input-group>
                            </flex-container>
                        </template>
                    </card-expandable>
                </flex-container>
            </template>
            <template #footer>
                <dialog-footer>
                    <button-block
                        :disabled="!quickOptionsBindings?.isValid.value"
                        without-loading
                        @click="addQuickOption(quickOptionsBindings?.isValid.value)"
                    >
                        Toevoegen
                    </button-block>
                </dialog-footer>
            </template>
        </modal-dialog>
    </form-validation>
</template>

<script setup lang="ts">
    import {
        CountryFlag,
        type ExposedOverlay,
        FlexContainer,
        FontSize,
        FormValidation,
        IconSize,
        InputGroup,
        Size,
        SwitchInput,
        TextDecorator,
        TextDescription,
        TextHeading,
    } from '@keyvue/one-ui';
    import MessageMinusIcon from '@keyvue/one-awesome-icons/icons/regular/message-minus.svg?component';
    import MessageLinesIcon from '@keyvue/one-awesome-icons/icons/regular/message-lines.svg?component';
    import { type Ref, ref, watch } from 'vue';
    import { type Assistent, QuickOption, QuickOptionId } from '@keyvue/one-api-client';
    import QuickOptionsList from '@/ai/assistent/components/forms/quick-options/QuickOptionsList.vue';
    import AddIcon from '@keyvue/one-material-icons/icons/add.svg?component';
    import InfoIcon from '@keyvue/one-material-icons/icons/info.svg?component';
    import { boolean, string } from 'yup';

    const props = defineProps<{ modelValue: Assistent }>();
    const emit = defineEmits<{
        (e: 'update:modelValue', value: Assistent): void;
    }>();

    const assistent = ref(props.modelValue) as Ref<Assistent>;
    const openQuickOptionModal = ref<ExposedOverlay>();

    export interface Form {
        shortMessage: string,
        message: string,
        englishShortMessage: string | null,
        englishMessage: string | null,
    }

    const form = ref<Form>({
        shortMessage: '',
        message: '',
        englishShortMessage: null,
        englishMessage: null,
    });

    watch(props.modelValue, (value) => {
        assistent.value = value;
    });

    watch(assistent, (value: Assistent) => {
        emit('update:modelValue', value);
    }, { deep: true });

    function addQuickOption(isValid: boolean) {
        if (!isValid) {
            return;
        }

        assistent.value.widget.quickOptions.push(
            new QuickOption(
                assistent.value.widget,
                QuickOptionId.generate(),
                form.value.shortMessage,
                form.value.message,
                form.value.englishShortMessage,
                form.value.englishMessage,
            ),
        );

        openQuickOptionModal.value?.closeOverlay();
        clearForm();
    }

    function deleteQuickOption(index: number) {
        assistent.value.widget.quickOptions.splice(index, 1);
    }

    function clearForm() {
        form.value.shortMessage = '';
        form.value.message = '';
        form.value.englishShortMessage = '';
        form.value.englishMessage = '';
    }
</script>
